import type { Component, ReploComponentType } from "schemas/component";

import * as React from "react";

import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import { Group } from "@v1/components/common/designSystem/Group";
import Input from "@v1/components/common/designSystem/Input";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { DEBUG_PANEL_WIDTH } from "@v1/components/editor/constants";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import { selectProductData } from "@v1/reducers/commerce-reducer";
import {
  selectComponentMapping,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
} from "@v1/reducers/core-reducer";
import { selectTemplateEditorStoreProduct } from "@v1/reducers/template-reducer";
import { selectThemeId, setThemeId } from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { calculateDependencies } from "@v1/utils/dependencies";
import { useAdminPanelStore } from "@v2/features/admin/adminPanelStore";
import { getAdminSections } from "@v2/features/admin/getAdminSections";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { X } from "lucide-react";
import {
  DEFAULT_ACTIVE_CURRENCY,
  DEFAULT_ACTIVE_LANGUAGE,
  DEFAULT_MONEY_FORMAT,
  DEFAULT_MONEY_WITH_CURRENCY_FORMAT,
} from "replo-runtime/shared/liquid";
import { allMediaSizeStyles } from "replo-runtime/shared/utils/breakpoints";
import { forEachComponentAndDescendants } from "replo-runtime/shared/utils/component";
import { recordEntries } from "replo-runtime/shared/utils/object";
import { ReploComponentTypes } from "schemas/component";

const ThemeIdBlock: React.FC = () => {
  const themeId = useEditorSelector(selectThemeId);
  const dispatch = useEditorDispatch();

  return (
    <div className="flex flex-col gap-3 py-1">
      <div className="text-muted text-xs">Override theme ID.</div>
      <Input
        value={themeId ?? undefined}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(setThemeId(e.target.value));
        }}
      />
    </div>
  );
};

const SelectTypeComponentFinderBlock: React.FC = () => {
  const [type, setType] = React.useState<ReploComponentType>("rawHtmlContent");
  const prioritizedTypes: ReploComponentType[] = [
    "rawHtmlContent",
    "shopifyRawLiquid",
  ];
  const sortedTypes = [
    ...ReploComponentTypes.filter((t) => !prioritizedTypes.includes(t)),
  ].sort();
  sortedTypes.unshift(...prioritizedTypes);
  return (
    <div className="flex flex-col gap-2">
      <Combobox
        options={sortedTypes.map((type) => ({
          value: type,
          label: type,
        }))}
        value={type}
        onChange={(value: string) => {
          setType(value as ReploComponentType);
        }}
        areOptionsSearchable
        side="right"
        trigger={
          <Combobox.SelectionButton
            size="sm"
            title={type}
            titleAlignment="start"
            startEnhancer={
              <Badge
                type="character"
                character={type.charAt(0).toUpperCase()}
              />
            }
          />
        }
      />
      <ComponentTypeFinderBlock types={[type]} />
    </div>
  );
};

const ComponentTypeFinderBlock: React.FC<{
  types: ReploComponentType[];
  test?: (component: Component) => boolean;
}> = ({ types, test }) => {
  const draftElement = useEditorSelector(
    selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  );
  const setDraftElement = useSetDraftElement();

  if (!draftElement) {
    return <span>No element selected!</span>;
  }

  const relevantComponents: Component[] = [];
  forEachComponentAndDescendants(draftElement.component, (component) => {
    if (types.includes(component.type)) {
      if (!test) {
        relevantComponents.push(component);
      } else {
        if (test(component)) {
          relevantComponents.push(component);
        }
      }
    }
  });

  return (
    <div className="flex flex-col gap-2">
      <p className="text-muted text-xs">
        Total components: {relevantComponents.length}
      </p>
      <div className="ml-2 flex max-h-[400px] flex-col gap-1 overflow-auto">
        {relevantComponents.map((component) => {
          return (
            <p
              key={component.id}
              onClick={() => {
                setDraftElement({
                  componentIds: [component.id],
                });
              }}
              className="text-muted cursor-pointer text-xs underline"
            >
              {component.name ?? component.type}
            </p>
          );
        })}
      </div>
    </div>
  );
};

const ComponentsWithDataSvgsBlock: React.FC = () => {
  return (
    <ComponentTypeFinderBlock
      types={["image"]}
      test={(component: Component) => {
        for (const mediaPropsKey of allMediaSizeStyles) {
          if (
            component.props[mediaPropsKey] &&
            component.props[mediaPropsKey]!.__imageSource?.includes(
              "data:image/svg",
            )
          ) {
            return true;
          }
        }
        if (
          component.props.src &&
          typeof component.props.src === "string" &&
          component.props.src.includes("data:image/svg")
        ) {
          return true;
        }
        return false;
      }}
    />
  );
};

const ProductDependenciesFinderBlock: React.FC = () => {
  const draftElement = useEditorSelector(
    selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  );
  const componentMapping = useEditorSelector(selectComponentMapping);
  const productData = useEditorSelector(selectProductData);
  const templateProduct =
    useEditorSelector(selectTemplateEditorStoreProduct) ?? null;
  const setDraftElement = useSetDraftElement();

  if (!draftElement) {
    return <span>No element selected!</span>;
  }

  const { dependencies } = calculateDependencies(draftElement.component, {
    productResolutionDependencies: {
      products: Object.values(productData),
      currencyCode: DEFAULT_ACTIVE_CURRENCY,
      language: DEFAULT_ACTIVE_LANGUAGE,
      moneyFormat: DEFAULT_MONEY_FORMAT,
      moneyWithCurrencyFormat: DEFAULT_MONEY_WITH_CURRENCY_FORMAT,
      templateProduct,
      isEditor: true,
      isShopifyProductsLoading: false,
    },
    metafieldsNamespaceKeyTypeMapping: { product: {}, variant: {} },
    templateProductId: templateProduct?.id ?? null,
  });
  const productIdToComponentIds: Record<string | number, string[]> = {};
  Object.entries(dependencies).forEach(([componentId, dependencies]) => {
    dependencies.forEach((dependency) => {
      if (dependency.type === "products") {
        dependency.productIds.forEach((productId) => {
          if (!productIdToComponentIds[productId]) {
            productIdToComponentIds[productId] = [];
          }
          productIdToComponentIds[productId]!.push(componentId);
        });
      }
    });
  });

  return (
    <div className="flex flex-col gap-2">
      <p className="text-muted text-xs">
        Total products referenced: {Object.keys(productIdToComponentIds).length}
      </p>
      {recordEntries(productIdToComponentIds).map(
        ([productId, componentIds]) => {
          return (
            <DeprecatedLabeledControl
              key={productId}
              label={productData[productId]?.title ?? "Unknown product"}
            >
              <div className="ml-2 flex flex-col gap-1">
                {componentIds.map((componentId) => {
                  return (
                    <p key={componentId} className="text-muted text-xs">
                      Referenced by{" "}
                      <span
                        onClick={() => {
                          setDraftElement({
                            componentIds: [componentId],
                          });
                        }}
                        className="cursor-pointer underline"
                      >
                        {componentMapping[componentId]?.component.name ??
                          componentMapping[componentId]?.component.type ??
                          `Unknown Component ${componentId}`}
                      </span>
                    </p>
                  );
                })}
              </div>
            </DeprecatedLabeledControl>
          );
        },
      )}
    </div>
  );
};

const ComponentSearchBlock: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const draftElement = useEditorSelector(
    selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  );
  const setDraftElement = useSetDraftElement();

  if (!draftElement) {
    return null;
  }

  const relevantComponents: Component[] = [];
  forEachComponentAndDescendants(draftElement.component, (component) => {
    if (
      searchTerm &&
      ((component.name &&
        component.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        component.id.includes(searchTerm))
    ) {
      relevantComponents.push(component);
    }
  });

  return (
    <div className="flex flex-col gap-2">
      <Input
        placeholder="Enter component name or id"
        onChange={(e) => setSearchTerm(e.target.value)}
        value={searchTerm}
        size="base"
        endEnhancer={() => (
          <DeprecatedIconButton
            variant="tertiary"
            size="sm"
            tooltipText="Clear Search"
            layoutClassName="h-4 w-4"
            icon={<X size={12} />}
            onClick={() => setSearchTerm("")}
          />
        )}
      />

      <div className="ml-2 flex max-h-[200px] flex-col gap-1 overflow-auto">
        {relevantComponents.map((component) => {
          return (
            <p
              key={component.id}
              onClick={() => {
                setDraftElement({
                  componentIds: [component.id],
                });
              }}
              className="text-muted cursor-pointer text-xs underline"
            >
              {component.name ?? component.type}
            </p>
          );
        })}
      </div>
    </div>
  );
};

const V1DebugPanelInner: React.FC = () => {
  const isDebugModeAllowed = useIsDebugMode();

  const v1OnlySections = [
    {
      component: <ThemeIdBlock />,
      title: "Theme Id",
    },
    {
      component: <SelectTypeComponentFinderBlock />,
      title: "Component Finder",
    },
    {
      component: <ComponentSearchBlock />,
      title: "Component Search",
    },
    {
      component: <ProductDependenciesFinderBlock />,
      title: "Product Dependencies",
    },
    {
      component: <ComponentsWithDataSvgsBlock />,
      title: "Components With data:svg",
    },
    {
      component: (
        <ComponentTypeFinderBlock
          types={["shopifyRawLiquid", "rawHtmlContent"]}
        />
      ),
      title: "Shopify Raw Liquid Components",
    },
    {
      component: <ComponentTypeFinderBlock types={["shopifySection"]} />,
      title: "Shopify Sections Components",
    },
  ];

  const sections = [...getAdminSections(), ...v1OnlySections];

  if (!isDebugModeAllowed) {
    return null;
  }

  return sections.map(({ component, title }) => (
    <div className="px-3 py-4" key={title}>
      <Group name={title} isCollapsible isDefaultOpen>
        <div className="border-l-border ml-2 border-l pl-3">{component}</div>
      </Group>
    </div>
  ));
};

export const V1DebugLeftPanel: React.FC = () => {
  const isVisible = useAdminPanelStore((s) => s.isVisible);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="z-10 flex">
      <div className="relative flex h-full">
        <div
          className="flex-1 flex-col overflow-y-scroll bg-white"
          style={{
            width: DEBUG_PANEL_WIDTH,
          }}
        >
          <V1DebugPanelInner />
        </div>
      </div>
    </div>
  );
};
